import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ContainedButton } from "components/ContainedButton";
import { DropDown } from "components/dropdown";
import { DynamicTool } from "@langchain/core/tools";
import React, { FC, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Control, Controller, UseFormHandleSubmit } from "react-hook-form";
import { useQuery } from "react-query";
import { getValidAccessToken } from "services/auth";
import { InferenceForm } from "../types";
import { ChatOpenAI } from "@langchain/openai";
import { HumanMessage, AIMessage, BaseMessage } from "@langchain/core/messages";
import { useTranslation } from "react-i18next";

import { createAgent } from "langchain";
import { MemorySaver } from "@langchain/langgraph";
import { getModelList } from "services/inference/getModelList";
import { toolList } from "../utils/toolList";
import { useUserInformationContext } from "contexts/userContext";

const useStyles = makeStyles((theme) => ({
  container: {
    overflow: 'hidden',
    gap: '15px',
    padding: theme.spacing(3),
    display: "flex",
  },
  paperContainer: {
    maxHeight: '100%',
    height: '100%',
    padding: '30px 20px'
  },
  input: {
    height: "150px",
    color: '#000000',
    padding: 5,
    border: "1.2px solid #000000",
    borderRadius: '5px',
    overflow: "scroll",
    fontSize: '0.875rem',
    '& .MuiInputBase-inputMultiline': {
      height: "100% !important"
    }
  },
  textarea: {
    height: "150px",
    color: '#000000',
    padding: 5,
    border: "1.2px solid #c6c6c6",
    borderRadius: '5px',
    minWidth: "100%",
    maxWidth: "100%",
    fontFamily: "inherit",
    fontSize: '0.875rem',
  },
  dropzoneContainer: {
    borderWidth: 1.2,
    borderRadius: 2,
    borderColor: '#c6c6c6',
    borderStyle: 'dashed',
    padding: theme.spacing(1),
  },
  fileContainer: {
    borderRadius: '5px',
    background: '#ececec',
    width: 'auto',
    position: "relative",
    padding: "5px"
  },
  removeFileContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: '#646b6e',
  },
  panelcontainer: {
    padding: "10px"
  },
  itemText: {
    color: "black", marginBottom: "5px"
  }
}));

type Props = {
  control: Control<InferenceForm, any>,
  setCurrentTab: React.Dispatch<React.SetStateAction<string>>,
  handleSubmit: UseFormHandleSubmit<InferenceForm>,
  setThoughts: React.Dispatch<React.SetStateAction<string>>,
  setOutput: React.Dispatch<React.SetStateAction<string>>,
  isInferenceLoading: boolean,
  setIsInferenceLoading: React.Dispatch<React.SetStateAction<boolean>>
}

export const Form: FC<Props> = ({ control, setCurrentTab, handleSubmit, setThoughts, setOutput, isInferenceLoading, setIsInferenceLoading }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [modelList, setModelList] = useState<{ value: string }[]>([]);
  const { currentUser } = useUserInformationContext();

  useQuery([], getModelList, {
    onSuccess: (response) => {
      if (response.data) {
        setModelList(response.data.map(d => ({ value: d.id })));
      }
    }
  });

  const { getInputProps, getRootProps, open } = useDropzone({
    maxSize: 31457280,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    noClick: true,
    noKeyboard: true,
    onDropAccepted: (files: File[]) => setFilesToUpload(prevFilesToUpload => ([...prevFilesToUpload, ...files])),
  });

  const onRemoveFile = ({ file }: { file: File }) => {
    setFilesToUpload((prevFilesToUpload) => prevFilesToUpload.filter(
      (fileToUpload) => fileToUpload.name !== file.name
    ));
  };

  const invoke = handleSubmit(async (data) => {
    const accessToken = await getValidAccessToken();
    const llm = new ChatOpenAI({
      configuration: {
        baseURL: import.meta.env.VITE_LLM_PROXY,
      },
      modelName: data.llmModel,
      openAIApiKey: accessToken,
      temperature: 0.9,
      apiKey: accessToken,
    });

    const tools = [
      ...toolList,
      new DynamicTool({
        name: "userInfo",
        description: "Returns all information about the user in JSON format.",
        func: async _ => {
          return JSON.stringify(currentUser, null, 2);
        }
      })
    ];

    try {
      const agent = createAgent({
        model: llm,
        tools,
        systemPrompt: data.systemMessage,
      });

      setCurrentTab("thoughts");
      setThoughts("");
      setOutput("");
      setIsInferenceLoading(true);

      const stream = await agent.stream(
        {
          messages: [
            new HumanMessage(data.executor)
          ],
        },
        { streamMode: "values" }
      );

      let finalOutput = "";
      // eslint-disable-next-line no-restricted-syntax
      for await (const chunk of stream) {
        const latestMessage = chunk.messages?.at(-1);
        if (latestMessage && latestMessage instanceof AIMessage && latestMessage.tool_calls?.length) {
          const toolCallNames = latestMessage.tool_calls.map((tc: any) => tc.name);
          const toolCallArgs = latestMessage.tool_calls.map((tc: any) => JSON.stringify(tc.args));
          setThoughts((prev) => `${prev}Tool: ${toolCallNames.join(", ")}\nInput: ${toolCallArgs.join(", ")}\n`);
        } else if (latestMessage?._getType?.() === 'tool') {
          const content = typeof latestMessage.content === 'string' ? latestMessage.content : JSON.stringify(latestMessage.content);
          setThoughts((prev) => `${prev} Output: ${content}\n\n`);
        } else if (latestMessage instanceof AIMessage && typeof latestMessage.content === 'string' && latestMessage.content) {
          finalOutput = latestMessage.content;
        }
      }

      setOutput(finalOutput);
    } catch (error: any) {
      setOutput(error!.message);
    }
    setCurrentTab("response");
    setIsInferenceLoading(false);
  });

  return (
    <Paper className={classes.paperContainer}>
      <Box style={{ display: "flex", gap: 10 }}>
        <Box style={{ width: "350px", display: "flex", flexDirection: "column", gap: "15px" }}>
          <Box>
            <Typography variant="body2" className={classes.itemText}>{t('inference:llmModelLabel')}</Typography>
            <Controller
              control={control}
              name="llmModel"
              render={({ field }) => (
                <DropDown
                  options={modelList}
                  defaultText={t('inference:selectLlmModel')}
                  optionText="value"
                  optionValue="value"
                  onChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </Box>

          <Box>
            <Typography variant="body2" className={classes.itemText}>{t('inference:imageUploadLabel')}</Typography>
            <Grid className={classes.dropzoneContainer}>
              {filesToUpload.length <= 0 && (
                <Grid {...getRootProps({ className: 'dropzone' })} style={{ cursor: 'pointer' }}>
                  <input {...getInputProps()} />

                  <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 10 }}>
                    <FontAwesomeIcon icon="upload" size="2x" />
                    <Typography style={{ textAlign: 'center', fontSize: "0.875rem" }}>{t('inference:dragDropText')}</Typography>
                    <Typography style={{ textAlign: 'center' }}>{t('common:or')}</Typography>
                    <ContainedButton
                      onClick={open}
                      text={t('inference:browseFiles')}
                    />
                  </Box>
                </Grid>
              )}
              <Grid container justifyContent="center" style={{ gap: '5px' }}>
                {filesToUpload.map((file) => (
                  <Grid item container className={classes.fileContainer} alignItems="center">
                    <Button onClick={() => onRemoveFile({ file })} style={{ minWidth: 'auto', position: "absolute", right: 0, top: 0 }}>
                      <Box className={classes.removeFileContainer}>
                        <FontAwesomeIcon
                          size="sm"
                          icon="plus"
                          color="white"
                          transform={{ rotate: 45 }}
                        />
                      </Box>
                    </Button>
                    <img key={file.name} src={URL.createObjectURL(file)} alt={file.name} width="130" height="100" style={{ objectFit: "contain" }} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="body2" className={classes.itemText}>{t('inference:executorLabel')}</Typography>
            <Controller
              control={control}
              name="executor"
              render={({ field }) => (
                <textarea
                  className={classes.textarea}
                  placeholder={t('inference:executorPlaceholder')}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          </Box>

          <ContainedButton text={t('inference:invokeButton')} onClick={invoke} isLoading={isInferenceLoading} icon="paper-plane" disabled={isInferenceLoading} />
        </Box>
      </Box>
    </Paper>
  );
};
